import { Location } from "@angular/common";
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Component, computed, OnInit, Signal, signal } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { SkeletonModule } from "primeng/skeleton";
import { CheckboxModule } from "primeng/checkbox";
import { MessageModule } from "primeng/message";
import { v4 as uuidv4 } from "uuid";
import { JobIssue, JobSignature, Media, SyncableStatus } from "../../../../core/database/db";
import { SELF_ASSIGNED_VENDOR, VendorDTO } from "../../../vendors/models/VendorDTO";
import ScheduleDetailHeader from "../shared/schedule-detail-header/schedule-detail-header.component";
import { PunchlistListComponent } from "../../../../shared/components/punchlist/punchlist.component";
import { ChecklistComponent } from "../../../../shared/components/checklist/checklist.component";
import { ImagesModalComponent } from "../../../media/components/images-modal/images-modal.component";
import { BqaImageItem } from "../../../media/components/images-modal/bqa-image-item";
import { BlobService } from "../../../../shared/services/blob.service";
import { ExtendedRouter } from "../../../../shared/services/extended-router.service";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { Dialog } from "@angular/cdk/dialog";
import { BqStepDataServiceFactory } from "../../../../personas/shared/bq-step-data-service/bq-step-data-service-factory";
import { BqInspectionDiscussAndDemonstrateDataService } from "../../services/bq-inspection-discuss-and-demonstrate-data.service";
import { UNASSIGNED_AREA } from "../../../areas/models/AreaDTO";
import { getDiscussAndDemonstrateHeaderTitle } from "../discuss-and-demonstrate-header-title";

type AreaNameIssue = JobIssue & { areaName?: string };

/**
 * Final-review step shared by BQ1/BQ5: step1 review punchlist (readonly), step2 review the
 * "reviewed with homeowner" checklist (readonly), step3 confirm & sign. Mirrors BQ8's bq8-final-review
 * flow, but every read/write goes through BqInspectionDiscussAndDemonstrateDataService.
 */
@Component({
  selector: "app-job-discuss-and-demonstrate-final-review",
  standalone: true,
  imports: [
    ScheduleDetailHeader,
    SkeletonModule,
    PunchlistListComponent,
    ChecklistComponent,
    CheckboxModule,
    ReactiveFormsModule,
    MessageModule
  ],
  providers: [BqStepDataServiceFactory],
  templateUrl: "./job-discuss-and-demonstrate-final-review.component.html",
  styleUrl: "./job-discuss-and-demonstrate-final-review.component.scss"
})
export class JobDiscussAndDemonstrateFinalReviewComponent implements OnInit {
  private bqStepDataService!: BqInspectionDiscussAndDemonstrateDataService;
  private readonly objectUrls = new Set<string>();

  jobId = computed(() => this.bqStepDataService?.job()?.id ?? -1);
  bqStep = computed(() => this.bqStepDataService?.bqStep() ?? "");
  headerTitle = computed(() => getDiscussAndDemonstrateHeaderTitle(this.bqStep()));
  step = signal(1);
  formSubmitted = signal(false);

  homeownerSignatures: FormGroup = new FormGroup({
    checklistConfirmation: new FormControl(false, Validators.requiredTrue),
    homeowner1Name: new FormControl(undefined, [Validators.required, Validators.pattern(".+"), this.noWhiteSpaceValidator]),
    homeowner1Email: new FormControl(undefined, [Validators.required, Validators.email]),
    homeowner2Name: new FormControl(undefined),
    homeowner2Email: new FormControl(undefined, Validators.email),
  }, { validators: this.homeowner2CrossValidator });

  dataLoaded = computed(() => this.bqStepDataService?.hasData() ?? false);
  subtitle = computed(() => {
    const lot = this.bqStepDataService?.lot();
    return lot ? `Lot ${lot.lotBlock} - ${lot.streetAddress}` : "";
  });

  checklistGroups = computed(() => this.bqStepDataService?.checklistGroups() ?? []);

  private readonly areaNameByIdentifier = computed<Record<string, string>>(() => {
    const dict: Record<string, string> = {};
    const areas = [...(this.bqStepDataService?.areas() ?? []), ...(this.bqStepDataService?.availableRooms() ?? [])];
    for (const area of areas) {
      dict[area.identifier] = area.name;
    }
    return dict;
  });

  readonly openIssues = computed<AreaNameIssue[]>(() => {
    const areaNameByIdentifier = this.areaNameByIdentifier();
    return (this.bqStepDataService?.openIssues() ?? [])
      .map(issue => ({
        ...issue,
        areaName: (issue.lotAreaIdentifier && areaNameByIdentifier[issue.lotAreaIdentifier]) || UNASSIGNED_AREA.name
      }))
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  });

  areaNames = computed<string[]>(() => {
    const names = new Set(this.openIssues().map(issue => issue.areaName)
      .filter((name): name is string => !!name));
    return [...names].sort();
  });

  issuesByArea = computed<Record<string, JobIssue[]>>(() => {
    const dict: Record<string, JobIssue[]> = {};
    for (const issue of this.openIssues()) {
      if (!issue.areaName) {
        continue;
      }
      (dict[issue.areaName] ??= []).push(issue);
    }
    return dict;
  });

  imagesOfIssue = computed<Record<number, Signal<(SafeUrl | undefined)[]>>>(() => {
    const dict: Record<number, Signal<(SafeUrl | undefined)[]>> = {};
    for (const issue of this.openIssues()) {
      if (issue.id === undefined) {
        continue;
      }
      dict[issue.id] = computed(() => this.resolveDisplayImages(issue.identifier));
    }
    return dict;
  });

  private galleryImagesOfIssue = computed<Record<number, Signal<BqaImageItem[]>>>(() => {
    const dict: Record<number, Signal<BqaImageItem[]>> = {};
    for (const issue of this.openIssues()) {
      if (issue.id === undefined) {
        continue;
      }
      dict[issue.id] = computed(() => this.resolveGalleryImages(issue.identifier));
    }
    return dict;
  });

  vendorOfIssue = computed<Record<number, Signal<VendorDTO>>>(() => {
    const dict: Record<number, Signal<VendorDTO>> = {};
    for (const issue of this.openIssues()) {
      if (issue.id === undefined) {
        continue;
      }
      const vendor = this.resolveVendorForIssue(issue);
      if (vendor) {
        dict[issue.id] = computed(() => this.resolveVendorForIssue(issue)!);
      }
    }
    return dict;
  });

  constructor(
    private route: ActivatedRoute,
    private router: ExtendedRouter,
    private location: Location,
    private sanitizer: DomSanitizer,
    private blobService: BlobService,
    private toastService: UiToastService,
    private dialog: Dialog,
    private bqStepDataServiceFactory: BqStepDataServiceFactory
  ) { }

  ngOnInit(): void {
    const bqStep = this.route.snapshot.parent?.data["bqStep"] as string;
    this.bqStepDataService = this.bqStepDataServiceFactory.getService<BqInspectionDiscussAndDemonstrateDataService>(bqStep);
  }

  openImagesModal(issueId: number, imageIndex: number): void {
    const galleryImages = this.galleryImagesOfIssue()[issueId];
    if (!galleryImages) {
      return;
    }

    this.dialog.open<string>(ImagesModalComponent, {
      data: {
        images: galleryImages(),
        index: imageIndex,
        isIssue: true,
        isComplete: true,
        backdropClass: "images-modal-backdrop"
      }
    });
  }

  goBack(): void {
    this.location.back();
  }

  nextStep(): void {
    if (this.step() === 3) {
      this.finishFinalReview();
      return;
    }
    this.step.update(step => step + 1);
    document.getElementById("step-text")?.scrollIntoView({ block: "start" });
  }

  previousStep(): void {
    if (this.step() === 1) {
      return;
    }
    this.step.update(step => step - 1);
    document.getElementById("step-text")?.scrollIntoView({ block: "start" });
  }

  isInvalid(controlName: string): boolean {
    const control = this.homeownerSignatures.get(controlName);
    return !!(control?.invalid && (control.touched || this.formSubmitted()));
  }

  async finishFinalReview(): Promise<void> {
    this.formSubmitted.set(true);
    if (!this.homeownerSignatures.valid) {
      return;
    }

    // Guards against a checklist item whose persistence failed and reverted after this flow was
    // already entered (see ChecklistComponent.itemChecked's revert-on-failure) - don't let the
    // inspector sign off on a walkthrough that's no longer actually complete.
    if (!this.bqStepDataService.checklistComplete()) {
      this.toastService.error("A checklist item failed to save and is no longer marked as reviewed. Please recheck the Checklist tab.");
      this.router.navigate(["/cm", this.bqStep().toLowerCase(), "job", this.jobId(), "list"]);
      return;
    }

    const signaturesForm = this.homeownerSignatures.getRawValue();
    const now = new Date();

    const signatures: JobSignature[] = [{
      jobId: this.jobId(),
      identifier: uuidv4(),
      signerName: signaturesForm.homeowner1Name,
      signerEmail: signaturesForm.homeowner1Email,
      signedAt: now,
      updatedAt: now,
      syncStatus: SyncableStatus.Pending
    }];

    if (signaturesForm.homeowner2Name && signaturesForm.homeowner2Email) {
      signatures.push({
        jobId: this.jobId(),
        identifier: uuidv4(),
        signerName: signaturesForm.homeowner2Name,
        signerEmail: signaturesForm.homeowner2Email,
        signedAt: now,
        updatedAt: now,
        syncStatus: SyncableStatus.Pending
      });
    }

    await this.bqStepDataService.saveFinalReviewSignatures(signatures);

    this.router.navigate(["/cm", this.bqStep().toLowerCase(), "job", this.jobId(), "submission"]);
  }

  isHomeowner2NameInvalid(): boolean {
    const nameControl = this.homeownerSignatures.get("homeowner2Name");
    const emailControl = this.homeownerSignatures.get("homeowner2Email");
    const touched = nameControl?.touched || emailControl?.touched || this.formSubmitted();
    return !!touched && !!this.homeownerSignatures.errors?.["homeowner2NameRequired"];
  }

  isHomeowner2EmailInvalid(): boolean {
    const emailControl = this.homeownerSignatures.get("homeowner2Email");
    const touched = emailControl?.touched || this.formSubmitted();
    if (!touched) {
      return false;
    }
    return !!emailControl?.errors?.["email"] || !!this.homeownerSignatures.errors?.["homeowner2EmailRequired"];
  }

  private homeowner2CrossValidator(group: AbstractControl) {
    const name = (group.get("homeowner2Name")?.value || "").trim();
    const email = (group.get("homeowner2Email")?.value || "").trim();

    const errors: { [key: string]: boolean } = {};
    if (email && !name) {
      errors["homeowner2NameRequired"] = true;
    }
    if (name && !email) {
      errors["homeowner2EmailRequired"] = true;
    }

    return Object.keys(errors).length ? errors : null;
  }

  private noWhiteSpaceValidator(control: AbstractControl) {
    const isWhitespace = (control.value || "").trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  private resolveDisplayImages(issueIdentifier: string): (SafeUrl | undefined)[] {
    return (this.bqStepDataService.media() ?? [])
      .filter(m => m.parentIdentifier === issueIdentifier && !m.deleted)
      .slice()
      .reverse()
      .map(img => this.toSafeUrl(img));
  }

  private resolveGalleryImages(issueIdentifier: string): BqaImageItem[] {
    return (this.bqStepDataService.media() ?? [])
      .filter(m => m.parentIdentifier === issueIdentifier && (m.base64Image || m.resourceUri) && !m.deleted)
      .slice()
      .reverse()
      .map(img => new BqaImageItem({
        azureBlobUri: this.resolveBlobUri(img),
        mediaIdentifier: img.fileIdentifier,
        isThumbnailOnly: img.isThumbnailOnly || false
      }));
  }

  private resolveVendorForIssue(issue: JobIssue): VendorDTO | undefined {
    if (issue.isSelfAssigned) {
      return SELF_ASSIGNED_VENDOR;
    }

    const jobIssueVendor = (this.bqStepDataService.jobIssueVendors() ?? [])
      .find(v => v.issueIdentifier === issue.identifier && !v.deleted);
    if (!jobIssueVendor) {
      return undefined;
    }

    return (this.bqStepDataService.vendors() ?? []).find(v => v.id === jobIssueVendor.externalVendorId);
  }

  private toSafeUrl(img: Media): SafeUrl | undefined {
    const uri = this.resolveBlobUri(img);
    return uri ? this.sanitizer.bypassSecurityTrustUrl(uri) : undefined;
  }

  private resolveBlobUri(img: Media): string {
    if (img.base64Image) {
      return this.createObjectUrlAndTrack(this.blobService.base64ToBlob(img.base64Image));
    }
    return img.resourceUri ?? "";
  }

  private createObjectUrlAndTrack(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.objectUrls.add(url);
    return url;
  }
}
